import React from "react";
import { Box, Typography, Button, Grid } from "@mui/material";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Sparkles, Cloud, Cpu, LineChart, LockKeyhole, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { fadeUp } from "../../utils/animations";

const platformCards = [
  { label: "Cloud", icon: Cloud },
  { label: "Security", icon: LockKeyhole },
  { label: "AI", icon: Cpu },
  { label: "Data", icon: LineChart },
];

const platformItems = ["AI roadmap generated", "Certificate QR verified", "Enterprise proposal routed"];

export default function HeroSection() {
  return (
    <Box
      component="section"
      sx={{
        bgcolor: "#F8FAFC",
        py: { xs: 8, md: 10 },
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 100%)",
      }}
    >
      <Box className="tech-grid" sx={{ position: "absolute", inset: 0, opacity: 0.18, pointerEvents: "none" }} />
      <Box sx={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "radial-gradient(circle at 50% 0%, rgba(0,86,210,0.10), transparent 42%)",
      }} />

      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, position: "relative" }}>
        <Grid
          container
          spacing={{ xs: 6, md: 4, lg: 6 }}
          alignItems="center"
          sx={{
            flexDirection: { xs: "column", md: "row" },
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          {/* Left */}
          <Grid item xs={12} md={6} sx={{ flexBasis: { md: "50%" }, maxWidth: { md: "50%" } }}>
            <motion.div {...fadeUp()}>
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 1,
                bgcolor: "rgba(255,255,255,0.85)", border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 999, px: 2, py: 1, mb: 3,
              }}>
                <Sparkles size={16} color="#2A8DFF" />
                <Typography variant="caption" fontWeight={600} color="text.secondary">Quality Gives Confidence</Typography>
              </Box>
            </motion.div>
            <motion.div {...fadeUp(0.08)}>
              <Typography variant="h1" fontWeight={700} sx={{ fontSize: { xs: "2.2rem", sm: "2.8rem", md: "3.2rem", lg: "3.8rem" }, lineHeight: 1.05, mb: 2.5 }}>
                Premium technology education for students and enterprise teams.
              </Typography>
            </motion.div>
            <motion.div {...fadeUp(0.16)}>
              <Typography variant="h6" color="text.secondary" fontWeight={400} lineHeight={1.8} sx={{ maxWidth: 560, mb: 4 }}>
                NetLabs ITS is a career accelerator, technology academy, cloud training provider, corporate training partner, and digital transformation consultant.
              </Typography>
            </motion.div>
            <motion.div {...fadeUp(0.24)}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button component={Link} to="/learn" variant="contained" size="large" endIcon={<ArrowRight size={18} />} sx={{ borderRadius: 2 }}>
                  Explore Learning Paths
                </Button>
                <Button component="a" href="#contact" variant="outlined" size="large" endIcon={<Phone size={18} />} sx={{ borderRadius: 2 }}>
                  Talk To Experts
                </Button>
              </Box>
            </motion.div>
          </Grid>

          {/* Right — platform card */}
          <Grid item xs={12} md={6} sx={{ flexBasis: { md: "50%" }, maxWidth: { md: "50%" } }}>
            <motion.div {...fadeUp(0.16)}>
              <Box sx={{ position: "relative" }}>
                <Box sx={{ position: "absolute", inset: -24, borderRadius: 5, bgcolor: "rgba(42,141,255,0.08)", filter: "blur(40px)" }} />
                <Box sx={{
                  position: "relative", borderRadius: 4,
                  border: "1px solid rgba(0,0,0,0.07)", bgcolor: "white", p: 1.5,
                  boxShadow: "0 4px 32px rgba(42,141,255,0.10)",
                }}>
                  <Box sx={{ borderRadius: 3, p: 3, background: "linear-gradient(135deg, #ffffff 0%, #EFF6FF 50%, #DBEAFE 100%)" }}>
                    <Box sx={{ bgcolor: "white", borderRadius: 2.5, p: 2, mb: 2.5, display: "inline-flex" }}>
                      <Box component="img" src="/netlabs-its-logo.png" alt="NetLabs ITS" sx={{ height: 48, width: "auto" }} />
                    </Box>
                    <Grid container spacing={1.5}>
                      {platformCards.map(({ label, icon: Icon }) => (
                        <Grid item xs={6} key={label}>
                          <Box sx={{ border: "1px solid rgba(255,255,255,0.4)", borderRadius: 2.5, p: 2.5, bgcolor: "rgba(255,255,255,0.5)", backdropFilter: "blur(8px)" }}>
                            <Icon size={26} color="#1d4ed8" />
                            <Typography variant="h6" fontWeight={700} mt={1.5} color="text.primary">{label}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <Box sx={{ mt: 2, border: "1px solid rgba(255,255,255,0.4)", borderRadius: 2.5, p: 2.5, bgcolor: "rgba(255,255,255,0.5)", backdropFilter: "blur(8px)" }}>
                      <Typography variant="overline" fontWeight={700} sx={{ letterSpacing: "0.18em", color: "text.secondary", display: "block", mb: 1.5 }}>
                        Platform Intelligence
                      </Typography>
                      {platformItems.map((item) => (
                        <Box key={item} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "rgba(255,255,255,0.6)", borderRadius: 2, px: 2, py: 1.5, mb: 1 }}>
                          <Typography variant="body2" fontWeight={600} color="text.primary">{item}</Typography>
                          <CheckCircle2 size={18} color="#22C55E" />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}